import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Send, MessageSquare, FileText, GraduationCap, Mail, Trash2 } from "lucide-react";

const QUICK_PROMPTS = [
  { label: "Draft a follow-up email", prompt: "Draft a warm professional follow-up email to a prospect named John Davies at NorthBay Credit Union — we had a discovery call about Customer Service Excellence training. Keep under 180 words.", icon: Mail },
  { label: "Outline a workshop", prompt: "Design a 1-day workshop outline on Leadership Development for mid-level managers. Include 4-6 learning objectives, modular agenda with timing, interactive activities, and assessment.", icon: GraduationCap },
  { label: "Suggest upsell services", prompt: "I just delivered a Customer Service Excellence workshop for a credit union. Suggest 3 logical follow-up services and how to position each in a one-paragraph re-engagement email.", icon: Sparkles },
  { label: "Summarize meeting notes", prompt: "Help me summarize the following raw meeting notes into a structured client recap with: key insights, identified needs, recommended next steps. (I'll paste notes after this.)", icon: FileText },
];

const Bubble = ({ role, content }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"} ccs-fade-up`}>
    <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${
      role === "user"
        ? "bg-[#5D3A1F] text-white"
        : "bg-white border border-[#EBE3D2] text-[#2A1810]"
    }`}>
      {role === "user" ? (
        <div className="whitespace-pre-wrap text-sm leading-relaxed">{content}</div>
      ) : (
        <div className="ccs-prose text-sm">
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed m-0">{content}</pre>
        </div>
      )}
    </div>
  </div>
);

const AIAssistantPage = () => {
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    if (!text.trim()) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await api.post("/ai/chat", { session_id: sessionId, message: text, model });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠ Error: ${e?.response?.data?.detail || e.message}` }]);
    } finally { setLoading(false); }
  };

  const clear = () => setMessages([]);

  return (
    <div className="space-y-5" data-testid="ai-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> CCS AI Assistant
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mt-1.5">Your strategic thinking partner</h1>
          <p className="text-[#6B5944] mt-1.5">Draft proposals, design workshops, write follow-ups — grounded in the CCS philosophy.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger className="w-[200px] h-11 rounded-xl bg-white border-[#EBE3D2]" data-testid="ai-model-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="claude-sonnet-4-6">Claude Sonnet 4.5</SelectItem>
              <SelectItem value="gpt-5.2">GPT-5.2</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={clear} className="h-11 w-11 rounded-xl" data-testid="ai-clear-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {messages.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {QUICK_PROMPTS.map((q) => (
            <button key={q.label} onClick={() => send(q.prompt)} data-testid={`quick-prompt-${q.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="ccs-card ccs-card-hover p-5 text-left hover:border-[#C28A2E]/40">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#C28A2E]/12 flex items-center justify-center shrink-0">
                  <q.icon className="w-4 h-4 text-[#C28A2E]" strokeWidth={1.7} />
                </div>
                <div>
                  <div className="font-medium text-[#2A1810] text-sm">{q.label}</div>
                  <div className="text-xs text-[#6B5944] mt-1 leading-relaxed">{q.prompt.slice(0, 100)}…</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="ccs-card flex flex-col" style={{ minHeight: "60vh" }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ai-messages-container">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <div className="w-14 h-14 rounded-2xl bg-[#C28A2E]/12 flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-[#C28A2E]" strokeWidth={1.5} />
              </div>
              <div className="font-display text-lg font-medium text-[#2A1810]">Start a conversation</div>
              <p className="text-sm text-[#6B5944] mt-1 max-w-md">Pick a quick prompt above, or type your question below.</p>
            </div>
          ) : (
            messages.map((m, i) => (<Bubble key={i} role={m.role} content={m.content} />))
          )}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border border-[#EBE3D2] rounded-2xl px-5 py-3.5 text-sm text-[#6B5944]">
                <span className="inline-flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C28A2E] animate-pulse" />
                  Thinking…
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-[#EBE3D2] p-4">
          <div className="flex items-end gap-2">
            <Textarea
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }
              }}
              placeholder="Ask your CCS assistant anything… (Shift+Enter for newline)"
              rows={2}
              className="flex-1 rounded-xl border-[#EBE3D2] resize-none"
            />
            <Button onClick={() => send(input)} disabled={loading || !input.trim()} className="bg-[#5D3A1F] hover:bg-[#4A2E18] h-11 px-5 rounded-xl" data-testid="ai-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantPage;
